import { withDndHtml5Backend } from '@doltech/core/lib/hooks/withDndHtml5Backend';
import { routingUrls } from '@doltech/core/lib/routing/routing.config';
import { DoTestWithPreview } from '@doltech/modules/lib/student-do-test/DoTestWithPreview';
import { ContinueOrStarOverModal } from '@doltech/modules/lib/student-do-test/layouts/ContinueOrStartoverModal';
import {
  getInitialStateOfTestSubmissionStore,
  useTestSubmissionStore,
} from '@doltech/modules/lib/student-do-test/model/test-submissions.hook';
import { TestSubmissionStore } from '@doltech/modules/lib/student-do-test/model/test-submissions.store';
import { DynamicPageProps } from '@doltech/routeDefinition';
import { useUserAuthentication } from '@doltech/ui/lib/figma/Auth/hocs/withUserAuthentication';
import {
  useDolPremiumStore,
  withDolPremiumStore,
} from '@doltech/ui/lib/hooks/dolPremium/withDolPremium';
import { compose } from '@doltech/utils/lib/compose';
import { observer } from 'mobx-react-lite';
import { applySnapshot, onAction } from 'mobx-state-tree';
import { useCreateStore, useProvider } from 'mobx-store-provider';
import { nanoid } from 'nanoid';
import { useRouter } from 'next/router';
import * as React from 'react';
import styled from 'styled-components';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';

const Main = styled.div.withConfig({
  componentId: 'DoTestTemplatePage_Main',
} as any)``;

interface DoTestTemplatePageProps extends DynamicPageProps {
  previewData: any;
}

const ACTION_REQUIRE_LOGIN = ['preventUserAction', 'goToQuestion'];

const withDoTest = (Component) => (props) => {
  const { previewData } = props;
  const doTestCreateStore = useCreateStore(
    TestSubmissionStore,
    getInitialStateOfTestSubmissionStore({
      data: previewData,
      isPreviewMode: true,
    })
  );

  const DoTestStoreProvider = useProvider(TestSubmissionStore);

  return (
    <DoTestStoreProvider value={doTestCreateStore}>
      <Component {...props} />
    </DoTestStoreProvider>
  );
};

const DoTestTemplatePage = observer((props: DoTestTemplatePageProps) => {
  const { internalPageLink } = props;
  const router = useRouter();
  const { forceLoginToProceedAction } = useUserAuthentication();
  const { isLoggedIn } = useGetAuthInfo();
  const doTestStore = useTestSubmissionStore();
  const premiumStore = useDolPremiumStore();
  const [renderKey, setRenderKey] = React.useState('');

  const [enabledContinueOrStartOver, setContinueOrStartOver] = React.useState(false);

  doTestStore.registerPremiumStore(premiumStore);

  React.useEffect(() => {
    doTestStore.setInternalPageLink(internalPageLink);
  }, [doTestStore, internalPageLink]);
  React.useEffect(() => {
    if (isLoggedIn && !premiumStore.isChecking) {
      setContinueOrStartOver(true);
      doTestStore.setPreviewMode(false);
    }
  }, [doTestStore, isLoggedIn, premiumStore.isChecking]);

  const handleStartTestSuccess = (data) => {
    setContinueOrStartOver(false);
    applySnapshot(doTestStore, getInitialStateOfTestSubmissionStore({ data }));
    doTestStore.setStartDoingTest();
    setRenderKey(nanoid());
  };

  const onSubmitSuccess = React.useCallback(async () => {
    router.replace(
      doTestStore.isFullTest
        ? routingUrls.landing.STUDENT_VIEW_RESULT_FULL_PATH(doTestStore.id)
        : routingUrls.landing.STUDENT_VIEW_RESULT_PRACTICE_PATH(doTestStore.id)
    );
  }, [doTestStore.id, doTestStore.isFullTest, router]);

  React.useEffect(() => {
    const dispose = onAction(doTestStore, (call) => {
      if (ACTION_REQUIRE_LOGIN.includes(call.name) && !isLoggedIn) {
        doTestStore.forcePauseAudio();
        forceLoginToProceedAction(() => {}, {
          title: '<span>Để <span class="red">làm bài</span> bạn cần phải đăng ký tài khoản!</span>',
          noRedirect: true,
        });
      }
    });
    return () => {
      dispose();
    };
  }, [doTestStore, forceLoginToProceedAction, isLoggedIn, premiumStore]);

  return (
    <>
      <ContinueOrStarOverModal
        onStartTestSuccess={handleStartTestSuccess}
        enabled={enabledContinueOrStartOver}
      />
      {premiumStore.isChecking ? null : (
        <Main>
          <DoTestWithPreview
            key={renderKey}
            onSubmitSuccess={onSubmitSuccess}
            skill={doTestStore?.skill}
          />
        </Main>
      )}
    </>
  );
});

export default compose(
  withDndHtml5Backend,
  withDolPremiumStore({
    onDismissExpiredModal: ({ props, historyRouter, router }) => {},
  }),
  withDoTest
)(DoTestTemplatePage);
